import React from 'react';
import { 
  HeartIcon, 
  PhoneIcon,
  MapPinIcon,
  CurrencyDollarIcon 
} from '@heroicons/react/24/outline';
import { contactInfo } from '../config/massSchedule';

interface QuickAccessButton {
  title: string;
  description: string;
  icon: React.ReactNode;
  action: () => void;
  bgColor: string;
  hoverColor: string;
}

const QuickAccessButtons: React.FC = () => {
  const buttons: QuickAccessButton[] = [
    {
      title: 'Online Giving',
      description: 'Support our church community',
      icon: <CurrencyDollarIcon className="h-8 w-8" />,
      action: () => window.open('https://donate.mydona.com/all-saints-thirsk', '_blank'),
      bgColor: 'bg-green-600',
      hoverColor: 'hover:bg-green-700'
    },
    {
      title: 'Prayer Requests',
      description: 'Submit your intentions',
      icon: <HeartIcon className="h-8 w-8" />,
      action: () => window.location.href = 'mailto:allsaintsthirsk@rcdmidd.org.uk?subject=Prayer Request',
      bgColor: 'bg-purple-600',
      hoverColor: 'hover:bg-purple-700'
    },
    {
      title: `Contact ${contactInfo.priestName}`,
      description: 'Speak with our Parish Priest',
      icon: <PhoneIcon className="h-8 w-8" />,
      action: () => window.location.href = `tel:+44${contactInfo.phone.replace(/\s/g, '')}`,
      bgColor: 'bg-red-600',
      hoverColor: 'hover:bg-red-700'
    },
    {
      title: 'Visit Us',
      description: 'Get directions to church',
      icon: <MapPinIcon className="h-8 w-8" />,
      action: () => window.open('https://www.google.com/maps/place/All+Saints+Catholic+Church/@54.23153,-1.3468156,549m/data=!3m2!1e3!4b1!4m5!3m4!1s0x487eb74739d9144b:0xcc8280275af43288!8m2!3d54.23153!4d-1.3446269?hl=en', '_blank'),
      bgColor: 'bg-indigo-600',
      hoverColor: 'hover:bg-indigo-700'
    },
    {
      title: 'Facebook',
      description: 'Follow our community updates',
      icon: (
        <svg className="h-8 w-8" fill="currentColor" viewBox="0 0 24 24">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
        </svg>
      ),
      action: () => window.open('https://www.facebook.com/AllSaintsThirsk/', '_blank'),
      bgColor: 'bg-blue-600',
      hoverColor: 'hover:bg-blue-700'
    },
    {
      title: 'YouTube',
      description: 'Watch our services & videos',
      icon: (
        <svg className="h-8 w-8" fill="currentColor" viewBox="0 0 24 24">
          <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
        </svg>
      ),
      action: () => window.open('https://www.youtube.com/@AllSaintsChurchThirsk', '_blank'),
      bgColor: 'bg-red-500',
      hoverColor: 'hover:bg-red-600'
    }
  ];

  return (
    <section className="py-16 bg-gray-50" aria-labelledby="quick-access-heading">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 id="quick-access-heading" className="text-3xl md:text-4xl font-serif font-bold text-church-dark mb-4">
            Quick Access
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Easy access to important parish services and information.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6">
          {buttons.map((button, index) => (
            <button
              key={index}
              onClick={button.action}
              className={`${button.bgColor} ${button.hoverColor} text-white p-6 rounded-lg shadow-md transition-all duration-200 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white group`}
              aria-describedby={`button-desc-${index}`}
            >
              <div className="flex flex-col items-center text-center">
                <div className="mb-4 p-3 bg-white bg-opacity-20 rounded-full group-hover:bg-opacity-30 transition-all duration-200">
                  {button.icon}
                </div>
                <h3 className="text-lg font-semibold mb-2">
                  {button.title}
                </h3>
                <p id={`button-desc-${index}`} className="text-sm opacity-90">
                  {button.description}
                </p>
              </div>
            </button>
          ))}
        </div>

        <div className="mt-12 text-center">
          <div className="bg-church-blue rounded-lg p-6 text-white max-w-2xl mx-auto">
            <h3 className="text-xl font-semibold mb-4">Need Help?</h3>
            <p className="mb-4">
              {contactInfo.priestName} is available to help with any questions or pastoral needs.
              Feel free to call or email if you need assistance with anything.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="tel:+441845527011"
                className="bg-white text-church-blue hover:bg-gray-100 font-semibold py-2 px-4 rounded transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-church-blue"
              >
                Call 01845 527011
              </a>
              <a
                href="mailto:allsaintsthirsk@rcdmidd.org.uk"
                className="border-2 border-white text-white hover:bg-white hover:text-church-blue font-semibold py-2 px-4 rounded transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-church-blue"
              >
                Send Email
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default QuickAccessButtons;